/**
 * The register's catalog model vs what the till itself reports
 * (`GET ext3 …/terminals/{id}/device-model`, sunmi-card-setup.md §5).
 *
 * `undefined` while reading, `null` when the read failed — callers render
 * nothing for either, never a guess.
 */
import { useEffect, useState } from 'react';
import { api } from '../../../services/apiService';

export interface DeviceModelReport {
  deviceModelId: string | null;
  model: { modelId: string; modelName: string; hardwareIds?: string[] } | null;
  reported: { hardwareModel?: string | null; buildTarget?: string | null } | null;
  mismatch: boolean;
  suggestedModels: Array<{ modelId: string; modelName: string; buildTarget?: string | null }>;
}

export function useDeviceModelReport(tenantId: string | null | undefined, terminalId: string | null | undefined, refreshKey = 0): DeviceModelReport | null | undefined {
  const [data, setData] = useState<DeviceModelReport | null | undefined>(undefined);
  useEffect(() => {
    let live = true;
    if (!tenantId || !terminalId) { setData(null); return; }
    setData(undefined);
    Promise.resolve()
      .then(() => api.posNmi.getTerminalDeviceModel(tenantId, terminalId))
      .then((r) => { if (live) setData(r && typeof r === 'object' && 'mismatch' in r ? (r as DeviceModelReport) : null); })
      .catch(() => { if (live) setData(null); });
    return () => { live = false; };
  }, [tenantId, terminalId, refreshKey]);
  return data;
}

/** The till's own model string, or null. */
export const reportedHardware = (r: DeviceModelReport | null | undefined): string | null =>
  (r?.reported?.hardwareModel || '').trim() || null;

/** Does `reported` name this catalog row (its `appProfile.hardwareIds`, case-insensitive)? */
export function modelClaims(row: { appProfile?: { hardwareIds?: string[] } | null; hardwareIds?: string[] } | null | undefined, reported: string | null | undefined): boolean {
  const said = (reported || '').trim().toLowerCase();
  if (!said) return false;
  const ids = row?.appProfile?.hardwareIds || row?.hardwareIds || [];
  return ids.some((h) => (h || '').trim().toLowerCase() === said);
}
